import { AutomationsPresentationV1 } from 'Automations/automationEditor/types'
import {
  getStartNode,
  hasBlockingValidationResults,
  presentationToApiPresentation,
  presentationToConductorWorkflow,
  validatePresentation
} from 'Automations/nodeDefinitions'
import {
  createConductorWorkflow,
  createNoopTask
} from 'Automations/nodeDefinitions/conductor'
import {
  NodeType,
  ResolvedNodeDefinitions
} from 'Automations/nodeDefinitions/types'

const DEFAULT_TRIGGER_TYPE = 'QUOTE_REQUIRES_APPROVAL' as const

// A SIMPLE task here would fail at runtime if Conductor ever tried to execute
// the workflow (no registered worker named "placeholder"). NOOP is inert, so
// the placeholder is safe to persist and ignore until the next valid save
// overwrites it.
const createPlaceholderWorkflow = ({
  name,
  description
}: {
  name: string
  description: string
}) =>
  createConductorWorkflow({
    name,
    description,
    inputParameters: [],
    tasks: [
      createNoopTask({
        name: 'placeholder',
        taskReferenceName: 'placeholder_noop',
        inputParameters: {
          message: 'invalid workflow, this is a placeholder'
        }
      })
    ]
  })

export const presentationToVersionParams = ({
  name,
  description,
  presentation,
  definitions
}: {
  name: string
  description: string
  presentation: AutomationsPresentationV1
  definitions: ResolvedNodeDefinitions
}) => {
  const apiPresentation = presentationToApiPresentation(presentation)
  const startNode = getStartNode(presentation, definitions)

  /**
   * If startNode is undefined (for example, when trying to save an empty workflow), use a default
   * trigger type and a no-op task to satisfy BE saving requirements
   */
  if (!startNode?.type) {
    return {
      apiPresentation,
      workflowDefinition: createConductorWorkflow({
        name,
        description,
        inputParameters: [],
        tasks: [
          createNoopTask({
            name: 'placeholder',
            taskReferenceName: 'placeholder_noop',
            inputParameters: {}
          })
        ]
      }),
      triggerType: DEFAULT_TRIGGER_TYPE
    }
  }

  const nodeDefinition = definitions[startNode.type as NodeType]
  if (!nodeDefinition) throw new Error('node definition not found')

  const triggerType = nodeDefinition.startNodeTriggerType
  if (!triggerType) throw new Error('triggerType is undefined')

  // Validation does not run on save, so an invalid workflow can reach this
  // point. A workflow with blocking validation results could not be published,
  // and transforming it is liable to produce a definition Conductor rejects
  // (e.g. a switch with a configured but unconnected branch yields a decision
  // case with no tasks). The transformation itself can also throw (e.g. a
  // switch condition with no property selected). In both cases substitute a
  // placeholder workflow so the save still succeeds and the user can keep
  // iterating; the next valid save will overwrite it.
  try {
    const validationResults = validatePresentation(presentation, definitions)

    if (hasBlockingValidationResults(validationResults)) {
      return {
        apiPresentation,
        workflowDefinition: createPlaceholderWorkflow({ name, description }),
        triggerType
      }
    }

    return {
      apiPresentation,
      workflowDefinition: presentationToConductorWorkflow({
        name,
        description,
        presentation,
        definitions
      }),
      triggerType
    }
  } catch (err) {
    console.error(err)
    return {
      apiPresentation,
      workflowDefinition: createPlaceholderWorkflow({ name, description }),
      triggerType
    }
  }
}
